<template>
    <div class="pb-4">
        <v-row no-gutters class="pt-2 mb-3">
            <v-btn size="small" prepend-icon="mdi-plus" variant="text" color="primary" @click="addCondition">
                {{ $t('add_condition') }}
            </v-btn>
        </v-row>
        <div
            v-for="(condition, index) in conditions"
            :key="`${condition.selectItemType}-${condition.condition}-${condition.valueType}`"
        >
            <v-row dense class="pt-2">
                <v-col cols="12" sm="10" md="4">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="condition.selectItemType"
                                :items="getSalesTypeList"
                                :label="$t('field')"
                                hide-details="auto"
                                @update:model-value="updateData(condition)"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="condition.condition"
                                :items="getValueTypeConditions(condition.valueType)"
                                :label="$t('condition')"
                                hide-details="auto"
                            />
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="12" sm="10" md="4">
                    <template v-if="condition.valueType === 'TEXT'">
                        <v-text-field
                            v-model="condition.value"
                            hide-details="auto"
                            :label="`${$t('value')}*`"
                            :rules="[requiredRule($t('value'))]"
                        />
                    </template>
                    <template v-if="condition.valueType === 'DATE'">
                        <template v-if="condition.condition === 'BETWEEN'">
                            <daterange
                                :start-date-label="`${$t('date_from')}*`"
                                :end-date-label="`${$t('date_to')}*`"
                                :min-start-date="getDateNow"
                                :single-line="false"
                                :clearable="true"
                                hide-details="auto"
                                same-date
                                :_start-date="getValidDate(condition.startDate)"
                                :_end-date="getValidDate(condition.endDate)"
                                :rules="[requiredRule('date')]"
                                @change-start-date="condition.startDate = $event"
                                @change-end-date="condition.endDate = $event"
                            />
                        </template>
                        <template v-else>
                            <date-picker
                                v-model="condition.startDate"
                                :min="getDateNow"
                                class="cursor-pointer"
                                hide-details="auto"
                                :label="`${$t('date')}*`"
                                outlined
                                clearable
                                color="primary"
                                :rules="[requiredRule('date')]"
                            />
                        </template>
                    </template>
                    <template v-if="condition.valueType === 'NUMBER'">
                        <v-number-input
                            v-model="condition.value"
                            :min="1"
                            :max="365"
                            hide-details="auto"
                            :label="`${$t('value')}*`"
                            :rules="[requiredRule($t('value'))]"
                        />
                    </template>
                    <template v-if="condition.valueType === 'SUPPLIER'">
                        <ConditionsSimpleSearchAutocomplete
                            v-model="condition.resolvedValue"
                            :label="`${$t('supplier_name')}*`"
                            :rules="[requiredRule($t('supplier_name'))]"
                            app-method="suppliers"
                            :search-type="condition.valueType"
                            search-key="name"
                            item-value="name"
                            item-text="name"
                            return-object
                            :default-request="{limit: 10}"
                            @update:model-value="
                                e => {
                                    condition.value = e?.code ? e.code : null
                                    condition.resolvedValue = e?.name ? e.name : null
                                }
                            "
                        />
                    </template>
                    <template v-if="condition.valueType === 'HOTEL_NAME'">
                        <ConditionsSimpleSearchAutocomplete
                            v-model="condition.value"
                            :label="`${$t('productType.accommodation')}*`"
                            :rules="[requiredRule($t('productType.accommodation'))]"
                            app-method="suggestHotels"
                            :search-type="condition.valueType"
                            search-key="pattern"
                            item-value="id"
                            item-text="name"
                            return-object
                            :default-request="{limit: 10, active: true}"
                            :initial-search-value="{pattern: condition.resolvedValue}"
                            @update:model-value="
                                e => {
                                    let elements = [e?.name, e?.location?.cityName, e?.location?.countryName]?.filter(
                                        element => element
                                    )
                                    condition.resolvedValue = elements?.length ? elements.join(', ') : null
                                    condition.value = e?.id ? e?.id : null
                                }
                            "
                        />
                    </template>
                    <template v-if="condition.valueType === 'CITY'">
                        <ConditionsSimpleSearchAutocomplete
                            v-model="condition.cityId"
                            :label="`${$t('city')}*`"
                            :rules="[requiredRule($t('city'))]"
                            app-method="locations"
                            :search-type="condition.valueType"
                            search-key="pattern"
                            item-value="id"
                            item-text="name"
                            return-object
                            :default-request="{limitCities: 10, orderBy: 'POPULARITY'}"
                            @update:model-value="
                                e => {
                                    condition.countryId = e?.countryId ? e.countryId : null
                                    condition.cityId = e?.id ? e.id : null
                                }
                            "
                        />
                    </template>
                    <template v-if="condition.valueType === 'COUNTRY'">
                        <ConditionsSimpleSearchAutocomplete
                            v-model="condition.value"
                            :label="`${$t('country')}*`"
                            :rules="[requiredRule($t('country'))]"
                            app-method="locations"
                            :search-type="condition.valueType"
                            search-key="pattern"
                            item-value="id"
                            item-text="name"
                            return-object
                            :default-request="{limitCountries: 10, orderBy: 'POPULARITY'}"
                            @update:model-value="
                                e => {
                                    condition.value = e?.id ? e.id : null
                                }
                            "
                        />
                    </template>
                    <template v-if="condition.valueType === 'COMPOSITE_LOCATION'">
                        <ConditionsSimpleSearchAutocomplete
                            v-model="condition.cityId"
                            :label="`${$t('destination')}*`"
                            :rules="[requiredRule($t('destination'))]"
                            app-method="locations"
                            :search-type="condition.valueType"
                            search-key="pattern"
                            item-value="id"
                            item-text="name"
                            return-object
                            :default-request="{
                                limitCities: 10,
                                limitCountries: 10,
                                orderBy: 'POPULARITY',
                            }"
                            @update:model-value="
                                e => {
                                    condition.countryId = e?.countryId ? e.countryId : null
                                    condition.cityId = e?.id ? e.id : null
                                }
                            "
                        />
                    </template>
                    <template v-if="condition.valueType === 'NUMBER_OF_GUESTS'">
                        <v-row dense>
                            <v-col cols="3">
                                <v-autocomplete
                                    v-model="condition.adults"
                                    :items="numbersWithZero"
                                    hide-details="auto"
                                    :label="`${$t('adults')}*`"
                                    :rules="[requiredRule($t('adults'))]"
                                />
                            </v-col>
                            <v-col cols="3">
                                <v-row no-gutters>
                                    <v-autocomplete
                                        v-model="condition.children"
                                        :items="numberOfChildren"
                                        hide-details="auto"
                                        :label="`${$t('children')}*`"
                                        :rules="[requiredRule($t('children'))]"
                                        @update:model-value="updateChildrenAgesRow(condition)"
                                    />
                                </v-row>
                            </v-col>
                            <v-col cols="1">
                                <v-icon v-tooltip="$t('condition_children_note')" size="small" start class="text-info">
                                    mdi-information-outline
                                </v-icon>
                            </v-col>
                            <v-col cols="5">
                                <template v-if="condition.ages">
                                    <v-row v-for="(age, ageIndex) in condition.ages" :key="ageIndex" dense>
                                        <v-col cols="6">
                                            <v-autocomplete
                                                v-model="age.minAge"
                                                :items="childrenAges"
                                                :label="`${$t('from')}*`"
                                                hide-details="auto"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <v-autocomplete
                                                v-model="age.maxAge"
                                                :items="childrenAges"
                                                :label="`${$t('to')}*`"
                                                hide-details="auto"
                                            />
                                        </v-col>
                                    </v-row>
                                </template>
                            </v-col>
                        </v-row>
                    </template>
                    <template v-if="condition.valueType === 'FLIGHT_AIRLINE'">
                        <airlines-search-input
                            v-model="condition.value"
                            :label="`${$t('flights.airline')}*`"
                            outlined
                            :rules="[requiredRule($t('flights.airline'))]"
                            hide-details="auto"
                            order-by="ALPHABET"
                        />
                    </template>
                    <template v-if="condition.valueType === 'CURRENCY'">
                        <v-autocomplete
                            v-model="condition.value"
                            :items="currencyItems"
                            :label="`${$t('currency')}*`"
                            clearable
                            hide-details
                            :rules="[requiredRule($t('currency'))]"
                        />
                    </template>
                    <template v-if="condition.valueType === 'SERVICE_TYPE'">
                        <v-autocomplete
                            v-model="condition.value"
                            :items="availableProducts"
                            :label="`${$t('service_type')}*`"
                            clearable
                            hide-details
                            :rules="[requiredRule($t('service_type'))]"
                        />
                    </template>
                </v-col>
                <v-btn
                    size="small"
                    icon="mdi-close"
                    variant="text"
                    color="error"
                    class="d-block ml-auto ml-sm-0"
                    @click="removeCondition(index)"
                />
            </v-row>
        </div>
    </div>
</template>
<script setup lang="ts">
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import Daterange from '~/components/search/forms/Daterange.vue'
    import AirlinesSearchInput from '@/components/snippets/AirlinesSearchInput.vue'
    import {getMappedProductKeys} from '~/utils/authorization-info-utils'

    const props = defineProps<{
        conditionValueMap: object
        supportedCurrencies?: string[]
    }>()

    const conditions = defineModel<any>()

    const isLoading = ref<boolean>(false)
    const conditionItemTypes = ref({})
    const valueTypeConditions = ref({})

    const {$conf} = useNuxtApp()
    const {t} = useI18n()
    const conf = useRuntimeConfig()

    onMounted(async () => {
        await loadData()
    })

    async function loadData(): Promise<void> {
        isLoading.value = true

        if (Object.keys(supplierStore.valueTypeConditions).length === 0) await supplierStore.loadValueTypeConditions()
        valueTypeConditions.value = supplierStore.valueTypeConditions

        if (Object.keys(supplierStore.conditionItemTypes).length === 0) await supplierStore.loadConditionItemTypes()
        conditionItemTypes.value = supplierStore.conditionItemTypes

        isLoading.value = false
    }

    const getSalesTypeList = computed(() => {
        const {conditionValueMap} = props

        if (conditionValueMap) {
            return Object.keys(conditionValueMap)?.map(value => ({
                value,
                title: t(`salesConditionsValues.${value}`),
                valueType: conditionValueMap[value],
            }))
        }
        return []
    })

    const updateData = data => {
        const valueType = props.conditionValueMap[data.selectItemType]
        const condition = valueTypeConditions.value[valueType][0]
        const type = conditionItemTypes.value[data.selectItemType]

        const newData = {
            selectItemType: data.selectItemType,
            valueType,
            condition,
            type,
        }

        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        Object.keys(data).forEach(key => delete data[key])
        Object.assign(data, newData)
    }

    const getValueTypeConditions = (valueType: string) => {
        const conditions = valueTypeConditions.value[valueType]
        if (conditions) {
            return conditions?.map(value => ({
                value,
                title: t(`valueTypeConditions.${value}`),
            }))
        }
    }

    const addCondition = () => {
        const conditionValueMap = props.conditionValueMap
        const selectItemType = Object.keys(conditionValueMap)[0]
        const valueType = props.conditionValueMap[selectItemType]

        conditions.value.push({
            selectItemType,
            valueType,
            condition: valueTypeConditions.value[valueType][0],
            type: conditionItemTypes.value[selectItemType],
        })
    }

    const removeCondition = indexToRemove => {
        conditions.value.splice(indexToRemove, 1)
    }

    const getDateNow = computed(() => {
        const currentDate = new Date(Date.now())
        return currentDate.toLocaleDateString('sv-SE')
    })

    const getValidDate = date => {
        return date ? new Date(date) : null
    }

    const numbersWithZero = computed(() => {
        return Array.from(Array(21).keys())
    })

    const numberOfChildren = computed(() => {
        return Array.from(Array(11).keys())
    })

    const childrenAges = computed(() => {
        return Array.from(Array(18).keys())
    })

    const updateChildrenAgesRow = condition => {
        if (!condition.ages) condition.ages = []

        const childrenQuantity = condition.children
        const childrenAgesLength = condition.ages?.length
        if (childrenQuantity > childrenAgesLength) {
            const additionalChildren = childrenQuantity - childrenAgesLength
            for (let i = 0; i < additionalChildren; i++) {
                condition.ages.push({
                    minAge: 0,
                    maxAge: 0,
                })
            }
        }
        if (childrenQuantity < childrenAgesLength) {
            condition.ages = condition.ages.slice(0, childrenQuantity)
        }
    }

    const currencyItems = computed(() => {
        if (!props.supportedCurrencies?.length) return conf.public.currency.available
        return props.supportedCurrencies
    })

    const availableProducts = computed(() => {
        const mappedProductKeys = getMappedProductKeys($conf.public.activeProducts, authStore.availableProducts)
        return mappedProductKeys.map(value => ({
            value,
            title: t(`productType.${value}`),
        }))
    })
</script>
